import React from "react";
import { useSelector } from "react-redux";
import MattersBrowseList from "./MattersBrowseList";
import MatterFolderBrowser from "./MatterFolderBrowser";

// Owns Browse's own navigation stack (matters list vs. a drilled-into
// matter/Firm Docs folder tree). Full Scope AI starts at the matters list;
// Matter AI (matterID passed in) is locked to that one matter and never
// shows the matters list or Firm Docs — mirroring how the Search tab
// already narrows itself when matterID is set.
//
// The actual position (selected matter, folder path, filter/sort choices)
// lives in `browseState`/`onUpdateBrowseState`, owned by AiAddContextModal —
// not in local state here — so it survives the modal being closed and
// reopened (Mantine unmounts this whole subtree when the modal closes).
const BrowseTab = ({
	matterID,
	existingItems,
	selected,
	onToggle,
	onPreview,
	browseState,
	onUpdateBrowseState,
	pushBackStep,
}) => {
	// MatterDetail.js (the parent route for every Matter tab, including AI)
	// already fetches this for the current matter regardless of which tab is
	// active, so it's available here without a separate fetch.
	const matterDetails = useSelector((state) => state.matter.matterDetails);

	if (matterID) {
		const numericMatterID = Number(matterID);
		const matter = {
			id: numericMatterID,
			name:
				matterDetails?.id === numericMatterID
					? matterDetails.name
					: "This Matter",
			isFirmDocs: false,
		};
		return (
			<MatterFolderBrowser
				matter={matter}
				existingItems={existingItems}
				selected={selected}
				onToggle={onToggle}
				onPreview={onPreview}
				browseState={browseState}
				onUpdateBrowseState={onUpdateBrowseState}
				pushBackStep={pushBackStep}
			/>
		);
	}

	if (!browseState.selectedMatter) {
		return (
			<MattersBrowseList
				existingItems={existingItems}
				selected={selected}
				onToggle={onToggle}
				onPreview={onPreview}
				browseState={browseState}
				onUpdateBrowseState={onUpdateBrowseState}
				onSelectMatter={(matter) => {
					const isFirmDocs = matter.id === -1 || matter.id === 0;
					pushBackStep(() =>
						onUpdateBrowseState({ selectedMatter: null })
					);
					onUpdateBrowseState({
						selectedMatter: {
							id: matter.id,
							name: matter.name,
							clientName: matter.clientName,
							isFirmDocs,
						},
						contentRoot: isFirmDocs
							? matter.id === -1
								? "public"
								: "private"
							: "docs",
						pathSegments: [],
						folderSort: "nameAsc",
					});
				}}
			/>
		);
	}

	return (
		<MatterFolderBrowser
			matter={browseState.selectedMatter}
			onBack={() => onUpdateBrowseState({ selectedMatter: null })}
			existingItems={existingItems}
			selected={selected}
			onToggle={onToggle}
			onPreview={onPreview}
			browseState={browseState}
			onUpdateBrowseState={onUpdateBrowseState}
			pushBackStep={pushBackStep}
		/>
	);
};

export default BrowseTab;
